import { Button, Card, Progress, Table, Tag, Tooltip, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";

import { Context } from "../../../context";
import { useBank } from "./BankContext";
import { Fetch } from "../../../fetch";

const { Column } = Table;
const { Text } = Typography;

/**
 * "Why is the next level not showing up?" — per-level playability from
 * /v4/quiz-config/readiness. A level only opens when its APPROVED pool
 * reaches the level's question count; players who reach a blocked level
 * see "new questions coming soon" in the app (POOL_DRY).
 */
const ReadinessCard = () => {
  const { token, apiUrl } = useContext(Context);
  const { configKey } = useBank();

  const [levels, setLevels] = useState([]);
  const [grades, setGrades] = useState([]);
  const [playersByStep, setPlayersByStep] = useState({});
  const [ungraded, setUngraded] = useState(0);
  const [adaptiveEnabled, setAdaptiveEnabled] = useState(false);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/readiness?configKey=${configKey}`, token, (result) => {
      if (result && result.levels) {
        setLevels(result.levels);
        setGrades(result.grades || []);
        setPlayersByStep(result.playersByStep || {});
        setUngraded(result.ungradedQuestions || 0);
        setAdaptiveEnabled(!!result.adaptiveEnabled);
      }
    });
  }, [apiUrl, token, counter]);

  const blocked = levels.filter((l) => !l.ready && l.playersWaiting > 0);

  return (
    <Card
      title="Level readiness — can players actually play?"
      size="small"
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => setCounter((n) => n + 1)}
        >
          Refresh
        </Button>
      }
    >
      {blocked.length > 0 && (
        <Text type="danger" strong>
          {blocked
            .map(
              (l) =>
                `${l.playersWaiting} player(s) stuck at level ${l.level} — approve ${l.missing} more question(s)`
            )
            .join(" · ")}
        </Text>
      )}
      <Table
        dataSource={levels.map((l) => ({ ...l, key: l.level }))}
        pagination={false}
        size="small"
        style={{ marginTop: blocked.length ? 8 : 0 }}
      >
        <Column title="Level" dataIndex="level" width={65} />
        <Column
          title="Approved / needed"
          width={220}
          render={(_, r) => (
            <Tooltip title={`${r.approved} approved of ${r.needed} needed`}>
              <Progress
                percent={Math.min(100, Math.round((r.approved * 100) / (r.needed || 1)))}
                size="small"
                status={r.ready ? "success" : "exception"}
                format={() => `${r.approved}/${r.needed}`}
              />
            </Tooltip>
          )}
        />
        <Column
          title="Drafts waiting"
          width={110}
          render={(_, r) =>
            r.draft > 0 ? <Tag color="warning">{r.draft}</Tag> : 0
          }
        />
        <Column
          title="Players at level"
          width={120}
          render={(_, r) =>
            r.playersWaiting > 0 ? (
              <Tag color={r.ready ? "default" : "error"}>{r.playersWaiting}</Tag>
            ) : (
              0
            )
          }
        />
        <Column
          title="Status"
          render={(_, r) => {
            if (!r.active) return <Tag>disabled by override</Tag>;
            if (r.ready) return <Tag color="success">READY</Tag>;
            const fix =
              r.draft >= r.missing
                ? `approve ${r.missing} in Review tab`
                : r.draft > 0
                ? `approve ${r.draft} drafts + generate ${r.missing - r.draft} more`
                : `generate & approve ${r.missing}`;
            return (
              <Tag color="error">
                BLOCKED — {fix}
              </Tag>
            );
          }}
        />
      </Table>
      <div style={{ marginTop: 8 }}>
        <Text type="secondary">
          A blocked level shows players "नए सवाल जल्द आ रहे हैं!" in the app.
          Fix: Generate tab → drafts, then Review tab → approve until the bar
          is full.
        </Text>
      </div>

      {grades.length > 0 && (
        <>
          <Table
            title={() => (
              <Text strong>
                Adaptive pool health by grade{" "}
                <Tag color={adaptiveEnabled ? "success" : "default"}>
                  adaptive {adaptiveEnabled ? "ON" : "OFF"}
                </Tag>
                {ungraded > 0 && (
                  <Tag color="warning">
                    {ungraded} ungraded question(s) — run the grade backfill
                  </Tag>
                )}
              </Text>
            )}
            dataSource={grades.map((g) => ({ ...g, key: g.grade }))}
            pagination={false}
            size="small"
            style={{ marginTop: 16 }}
          >
            <Column title="Grade" width={70} render={(_, r) => `G${r.grade}`} />
            <Column title="Approved" dataIndex="approved" width={90} />
            <Column
              title="Drafts"
              width={80}
              render={(_, r) =>
                r.draft > 0 ? <Tag color="warning">{r.draft}</Tag> : 0
              }
            />
            <Column title="Retired" dataIndex="retired" width={80} />
            <Column
              title="Players at step"
              width={120}
              render={(_, r) => playersByStep[String(r.grade)] || 0}
            />
            <Column
              title="Status"
              render={(_, r) =>
                r.gradeReady ? (
                  <Tag color="success">READY</Tag>
                ) : (
                  <Tag color="error">
                    THIN — generate grade {r.grade} in Generate tab
                  </Tag>
                )
              }
            />
          </Table>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              Every grade should be READY before switching adaptive on —
              players at a thin grade get widened (easier/harder) questions
              until its pool fills.
            </Text>
          </div>
        </>
      )}
    </Card>
  );
};

export default ReadinessCard;
